/* =====================================================
   CRO-Fix-2026-05-11 (NEUE DATEI, Rev 3 nach ChatGPT-Review)
   Sticky Buy Bar — Web-Component

   Rev-3-Änderungen:
   - Auto-Scroll auf Variant-Wahl komplett entfernt (ChatGPT WARN #5):
     Mobile-Disruption + Safari-Momentum-Scroll-Konflikt. Variant-Wahl
     wird durch Puls auf der Sticky-Bar bestätigt — das reicht.
   - Form-Submit jetzt mit instance-Check (ChatGPT WARN #6).
   - aria-hidden wird synchron mit data-visible getoggelt (ChatGPT INFO A11y):
     Screenreader bekommen keine doppelte CTA-Ansage mehr.

   Aus Rev 2 (nach Grok) übernommen:
   - Native `change` Event auf <variant-selects> (Bubble)
   - MutationObserver auf Preis-Container (Dawns Section-Fetch-Replacement)
   - MutationObserver auf Hidden-Variant-ID-Input (Disable-State)
   - data-section-id gezielt für Queries
   - prefers-reduced-motion respektiert
   ===================================================== */

class StickyBuyBar extends HTMLElement {
  constructor() {
    super();
    this.formId = this.dataset.formId;
    this.triggerId = this.dataset.triggerId;
    this.sectionId = this.dataset.sectionId;
    this._onSubmit = this._onSubmit.bind(this);
    this._onVariantSelectChange = this._onVariantSelectChange.bind(this);
    this._refreshFromDom = this._refreshFromDom.bind(this);
  }

  connectedCallback() {
    this.form = document.getElementById(this.formId);
    this.trigger = document.getElementById(this.triggerId);
    this.submitBtn = this.querySelector('[data-sticky-submit]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.variantEl = this.querySelector('[data-sticky-variant-title]');
    this.variantIdInput = this.form?.querySelector('input[name="id"].product-variant-id')
      || this.form?.querySelector('input[name="id"]');

    if (!(this.form instanceof HTMLFormElement) || !this.trigger || !this.submitBtn) {
      console.warn('[sticky-buy-bar] missing form, trigger, or submit button');
      return;
    }

    // Initial aria-hidden (Bar nicht sichtbar bis Trigger aus Viewport scrollt)
    this.setAttribute('aria-hidden', 'true');

    this.submitBtn.addEventListener('click', this._onSubmit);

    const variantSelectsSelector = this.sectionId
      ? `variant-selects[data-section="${this.sectionId}"]`
      : 'variant-selects';
    document.querySelectorAll(variantSelectsSelector).forEach((vs) => {
      vs.addEventListener('change', this._onVariantSelectChange);
    });

    const priceContainer = this._findPriceContainer();
    if (priceContainer) {
      this.priceObserver = new MutationObserver(this._refreshFromDom);
      this.priceObserver.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      // Falls Dawn das Element via outerHTML ersetzt, brauchen wir Parent-Watch
      if (priceContainer.parentElement) {
        this.priceParentObserver = new MutationObserver(this._refreshFromDom);
        this.priceParentObserver.observe(priceContainer.parentElement, {
          childList: true,
        });
      }
    }

    if (this.variantIdInput) {
      this.formObserver = new MutationObserver(this._refreshFromDom);
      this.formObserver.observe(this.variantIdInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled'],
      });
    }

    this._setupVisibilityObserver();
  }

  disconnectedCallback() {
    this.submitBtn?.removeEventListener('click', this._onSubmit);
    document.querySelectorAll('variant-selects').forEach((vs) => {
      vs.removeEventListener('change', this._onVariantSelectChange);
    });
    this.visibilityObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.priceParentObserver?.disconnect();
    this.formObserver?.disconnect();
    clearTimeout(this._pulseTimer);
  }

  _findPriceContainer() {
    if (this.sectionId) {
      const exact = document.getElementById(`price-${this.sectionId}`);
      if (exact) return exact;
    }
    return document.querySelector('[id^="price-"]')
      || document.querySelector('.price');
  }

  _setupVisibilityObserver() {
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const ctaVisible = entry.isIntersecting;
        const visible = !ctaVisible;
        this.dataset.visible = visible ? 'true' : 'false';
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );
    this.visibilityObserver.observe(this.trigger);
  }

  _onSubmit(event) {
    event.preventDefault();
    if (!(this.form instanceof HTMLFormElement)) return;
    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  }

  _onVariantSelectChange() {
    // Dawn-Fetch läuft async. MutationObserver kümmert sich um den DOM-Sync.
    // Hier nur visuelle Bestätigung via Puls (kein Auto-Scroll mehr — Rev 3).
    this._pulse();
  }

  _refreshFromDom() {
    const priceContainer = this._findPriceContainer();
    if (priceContainer && this.priceEl) {
      const priceNode = priceContainer.querySelector('.price-item--regular, .price-item--sale')
        || priceContainer;
      const priceText = priceNode.textContent.trim().replace(/\s+/g, ' ');
      if (priceText) this.priceEl.textContent = priceText;
    }

    if (this.variantIdInput && this.submitBtn) {
      const hasVariant = !!this.variantIdInput.value;
      const explicitlyDisabled = this.variantIdInput.hasAttribute('disabled');
      this.submitBtn.disabled = !hasVariant || explicitlyDisabled;
    }

    if (this.variantEl) {
      const variantSelectsScope = this.sectionId
        ? `variant-selects[data-section="${this.sectionId}"]`
        : 'variant-selects';
      const labels = [];
      document
        .querySelectorAll(`${variantSelectsScope} input[type="radio"]:checked`)
        .forEach((input) => {
          if (input.value) labels.push(input.value);
        });
      document
        .querySelectorAll(`${variantSelectsScope} select`)
        .forEach((select) => {
          if (select.value) labels.push(select.value);
        });
      if (labels.length) {
        this.variantEl.textContent = labels.join(' · ');
      }
    }
  }

  _pulse() {
    this.dataset.pulse = 'true';
    clearTimeout(this._pulseTimer);
    this._pulseTimer = setTimeout(() => {
      delete this.dataset.pulse;
    }, 700);
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
